// eslint configured: rule not present in CRA build
import { useEffect, useRef, useState } from "react";
import { X, Image as ImgIcon, Link as LinkIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { client, apiMutate } from "@/lib/api";

const INK_55 = "var(--ink-55)";

// Derive a YouTube thumbnail directly from the URL — used as a fallback
// when the user pastes a URL but doesn't click the Fetch button.
function youtubeThumb(url) {
  if (!url) return null;
  const re = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{11})/;
  const m = url.match(re);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

export default function SwipeModal({ onClose, onSaved }) {
  const [mode, setMode] = useState("image");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [meta, setMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // --- Tag chip handling — mirrors IdeaModal behavior so the two flows feel identical
  const commitTag = () => {
    const t = tagDraft.trim().replace(/^#/, "");
    if (!t) return;
    if (!tags.includes(t)) setTags([...tags, t]);
    setTagDraft("");
  };
  const onTagChange = (e) => {
    const v = e.target.value;
    // Comma triggers chip creation, like the rest of the app
    if (v.endsWith(",")) {
      const fragment = v.slice(0, -1).trim().replace(/^#/, "");
      if (fragment && !tags.includes(fragment)) setTags([...tags, fragment]);
      setTagDraft("");
    } else {
      setTagDraft(v);
    }
  };
  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      commitTag();
    } else if (e.key === "Backspace" && !tagDraft && tags.length) {
      setTags(tags.slice(0, -1));
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await client.post("/swipe/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(r.data.url);
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || "Try a smaller image." });
    } finally { setUploading(false); }
  };

  const fetchMeta = async () => {
    if (!sourceUrl.trim()) return;
    setFetching(true);
    try {
      const r = await client.post("/swipe/preview", { url: sourceUrl });
      setMeta(r.data);
      if (!title) setTitle(r.data.title || "");
    } catch (err) {
      // Even if remote preview fails, we can still derive a YouTube thumb.
      const yt = youtubeThumb(sourceUrl);
      if (yt) {
        setMeta({ thumbnail: yt, title: title || "YouTube video", description: "" });
      } else {
        toast.error("Couldn't fetch preview", { description: "We'll still save the link." });
      }
    } finally { setFetching(false); }
  };

  const save = async () => {
    if (saving) return;
    // Commit any unsaved tag fragment so the user doesn't lose what they typed.
    const finalTags = tagDraft.trim()
      ? [...new Set([...tags, tagDraft.trim().replace(/^#/, "")])]
      : tags;

    // Pick the best available image URL, falling back to YouTube thumb so the
    // card never renders without a visual.
    let bestImage = null;
    if (mode === "image") bestImage = imageUrl || null;
    else bestImage = meta?.thumbnail || youtubeThumb(sourceUrl);

    setSaving(true);
    try {
      const body = {
        kind: mode,
        title: title.trim() || (meta?.title || "Untitled"),
        note,
        tags: finalTags,
        image_url: bestImage,
        source_url: mode === "url" ? sourceUrl : null,
        meta: mode === "url" ? meta : null,
      };
      await apiMutate("post", "/swipe", body);
      toast.success("Saved to Swipe File");
      onSaved?.();
    } catch (err) {
      toast.error("Couldn't save swipe", { description: err?.response?.data?.detail || "Please try again." });
    } finally { setSaving(false); }
  };

  const canSave = mode === "image" ? !!imageUrl : !!sourceUrl;
  const modeBtn = (active) => ({
    background: active ? "var(--ink)" : "transparent",
    color: active ? "var(--bg)" : INK_55,
    border: active ? "1px solid var(--ink)" : "1px solid var(--ink-22)",
  });

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={onClose} data-testid="swipe-modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="swipe-modal" style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}>
        <div className="flex items-center justify-between px-6 py-4 sticky top-0 z-10" style={{ background: "var(--surface)", borderBottom: "1px solid var(--ink-22)" }}>
          <h3 className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>Add to Swipe File</h3>
          <button onClick={onClose} className="btn-ghost" data-testid="swipe-modal-close" style={{ touchAction: "manipulation" }}><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="swipe-mode-image" onClick={() => setMode("image")} className="flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition" style={modeBtn(mode === "image")}>
              <ImgIcon size={14} /> Upload image
            </button>
            <button data-testid="swipe-mode-url" onClick={() => setMode("url")} className="flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition" style={modeBtn(mode === "url")}>
              <LinkIcon size={14} /> Paste URL
            </button>
          </div>

          {mode === "image" ? (
            <div>
              {imageUrl ? (
                <div className="relative">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${imageUrl}`} alt="" className="rounded-lg w-full aspect-video object-cover" style={{ border: "1px solid var(--ink-22)" }} />
                  <button onClick={() => setImageUrl("")} className="absolute top-2 right-2 bg-black/70 text-white rounded-md px-2 py-1 text-xs">Replace</button>
                </div>
              ) : (
                <button data-testid="swipe-upload-trigger" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full rounded-lg py-10 flex flex-col items-center gap-2 text-sm" style={{ border: "1px dashed var(--ink-22)", color: INK_55, touchAction: "manipulation" }}>
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <ImgIcon size={20} />}
                  {uploading ? "Uploading…" : "Click to upload (max 5MB)"}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} hidden data-testid="swipe-file-input" />
            </div>
          ) : (
            <div>
              <div className="flex gap-2">
                <input data-testid="swipe-url-input" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://youtube.com/..." className="input flex-1" />
                <button data-testid="swipe-fetch-btn" onClick={fetchMeta} disabled={fetching || !sourceUrl} className="btn-secondary" style={{ touchAction: "manipulation" }}>
                  {fetching ? <Loader2 className="animate-spin" size={14} /> : "Fetch"}
                </button>
              </div>
              {(meta || youtubeThumb(sourceUrl)) && (
                <div className="mt-3 p-3 rounded-lg flex gap-3" style={{ background: "var(--bg)", border: "1px solid var(--ink-22)" }}>
                  <img src={meta?.thumbnail || youtubeThumb(sourceUrl)} alt="" className="w-20 h-20 object-cover rounded flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate" style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 500" }}>{meta?.title || "Preview ready — click Fetch for title"}</div>
                    {meta?.description && <div className="text-xs mt-1 line-clamp-2" style={{ color: INK_55 }}>{meta.description}</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="eyebrow block mb-2">Title</label>
            <input data-testid="swipe-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="input" placeholder="Mr Beast hook reference" />
          </div>

          {/* Tags input — chip-style, identical to IdeaModal */}
          <div>
            <label className="eyebrow block mb-2">Tags</label>
            <div
              onClick={() => document.querySelector('[data-testid="swipe-tags-input"]')?.focus()}
              className="input flex flex-wrap items-center gap-1.5 cursor-text min-h-[42px]"
            >
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.16em]"
                  style={{ background: "rgba(216,168,87,0.10)", color: "var(--seal)", border: "1px solid rgba(216,168,87,0.30)" }}
                  data-testid={`swipe-tag-chip-${t}`}
                >
                  {t}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTags(tags.filter((x) => x !== t)); }}
                    style={{ color: "var(--seal)", touchAction: "manipulation" }}
                    aria-label={`Remove ${t}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                data-testid="swipe-tags-input"
                value={tagDraft}
                onChange={onTagChange}
                onKeyDown={onTagKey}
                placeholder={tags.length ? "" : "hook, thumbnail"}
                className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-0.5"
                style={{ color: "var(--ink)" }}
              />
            </div>
            <div className="text-[10px] mt-1.5" style={{ color: INK_55 }}>
              Tip: press <kbd>,</kbd> or <kbd>Enter</kbd> to add a tag.
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-2">Note</label>
            <textarea data-testid="swipe-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="input resize-none" rows={2} placeholder="Why this is useful" />
          </div>
        </div>

        <div
          className="flex justify-end gap-2 px-6 py-4 sticky bottom-0"
          style={{ background: "var(--surface)", borderTop: "1px solid var(--ink-22)", paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClose}
            className="btn-secondary"
            data-testid="swipe-cancel-btn"
            style={{ touchAction: "manipulation" }}
          >Cancel</button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={save}
            disabled={!canSave || saving}
            className="btn-primary disabled:opacity-50"
            data-testid="swipe-save-btn"
            style={{ touchAction: "manipulation" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
